import React, { useMemo, Fragment, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
} from '@tanstack/react-table';
import { getLotColumns, getBidderColumns } from './NewSalesResultsTableColumns';
import { SET_FEEDBACK_MESSAGES, SELECTED_LOTS_FOR_DISPUTES } from '../../../modules/reports/reducer';
import { fetchDefaultFeedbackMessages, removeFeedback } from '../../../modules/reports/thunks';
import './tanstack.css';

// Separate component for the nested lot table
function LotTable({ lots, columns, memberRef, channel }) {
  const lotsTable = useReactTable({
    data: lots || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <table style={{ width: '97%', marginLeft: '3%', fontSize: '14px', marginBottom: '20px' }}>
      <thead>
        {lotsTable.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map(header => {
              header.memberRef = memberRef;
              header.channel = channel;
              header.lots = lots;
              return (
                <th
                  key={header.id}
                  style={{
                    border: '1px solid #ccc',
                    padding: 10,
                    background: '#f5f5f5',
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        {lotsTable.getRowModel().rows.map(lotRow => (
          <tr key={lotRow.id} className="lot-row">
            {lotRow.getVisibleCells().map((cell, index) => (
              <td key={cell.id} className={`lot-column-width-${index}`} style={{ border: '1px solid #ccc', padding: 4, textAlign: 'left' }}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function NewSalesResultsTable({
  tableData,
  catalogs,
  showSalesResultsDetails,
  expandedFlags,
  setExpandedFlags,
  checkboxArray,
  setCheckboxArray,
  sorting,
  setSorting,
  canAddFeedback,
  setLoadingFeedbackModalData,
  setIsOpenFeedbackModal,
  setCurrentFeedbackLotRef,
  setBidderName,
  setUserRef,
  setIsOpenSendMessageModal,
  multiSession,
}) {
  const dispatch = useDispatch();
  const { houseRef } = useSelector(state => state.house);

  const toggleSingleSalesResult = (ref) => {
    setExpandedFlags(prev => ({
      ...prev,
      [ref]: !prev[ref]
    }));
  };

  const handleLotCheckbox = (e) => {
    const lotRef = e.currentTarget.attributes['data-lot'].value;
    const memberRef = e.currentTarget.attributes['data-member'].value;
    const isChecked = e.currentTarget.checked;

    setCheckboxArray(prevCheckboxes => {
      const newCheckboxes = [...prevCheckboxes];
      //  used to check for all checked behavior
      const memberCatalogs = catalogs.filter(catalog => catalog.memberRef === memberRef);
      const lotArray = memberCatalogs.map(lot => lot.lotRef);
      const currentIndex = newCheckboxes.indexOf(lotRef);

      if (isChecked && currentIndex === -1) {
        newCheckboxes.push(lotRef);
        let allChecked = 1;
        lotArray.forEach(lot => {
          if (newCheckboxes.indexOf(lot) === -1) {
            allChecked = 0;
          }
        });
        if (allChecked === 1) {
          document.getElementById(`member_${memberRef}`).checked = true;
        }
      } else if (!isChecked && currentIndex > -1) {
        newCheckboxes.splice(currentIndex, 1);
        let allChecked = 1;
        lotArray.forEach(lot => {
          if (newCheckboxes.indexOf(lot) === -1) {
            allChecked = 0;
          }
        });
        if (allChecked === 0) {
          document.getElementById(`member_${memberRef}`).checked = false;
        }
      }
      return newCheckboxes;
    });
  };

  const handleMemberCheckbox = (e) => {
    const memberRef = e.currentTarget.attributes['data-member'].value;
    const isChecked = e.currentTarget.checked;

    setCheckboxArray(prevCheckboxes => {
      const newCheckboxes = [...prevCheckboxes];
      const memberCatalogs = catalogs.filter(catalog => catalog.memberRef === memberRef);
      const lotArray = memberCatalogs.map(lot => lot.lotRef);

      lotArray.forEach(lotRef => {
        const currentIndex = newCheckboxes.indexOf(lotRef);
        if (isChecked && currentIndex === -1) {
          newCheckboxes.push(lotRef);
        } else if (!isChecked && currentIndex > -1) {
          newCheckboxes.splice(currentIndex, 1);
        }
      });

      return newCheckboxes;
    });
  };

  const handleEditFeedback = useCallback((lotRef, feedback) => {
    setCurrentFeedbackLotRef(lotRef);

    dispatch(fetchDefaultFeedbackMessages(houseRef, setLoadingFeedbackModalData, true));
    dispatch({
      type: SET_FEEDBACK_MESSAGES,
      payload: {
        lotRef,
        feedback
      },
    });
    dispatch({
      type: SELECTED_LOTS_FOR_DISPUTES,
      payload: {
        addItem: true,
        lotRef
      }
    });

    setIsOpenFeedbackModal(true);
  }, [dispatch, houseRef, setCurrentFeedbackLotRef, setIsOpenFeedbackModal]);

  const handleRemoveFeedback = useCallback((lotRef, memberID) => {
    dispatch(removeFeedback(houseRef, lotRef, memberID));
    setLoadingFeedbackModalData(false);
  }, [dispatch, houseRef]);

  const bidderColumns = useMemo(
    () => getBidderColumns({
      showSalesResultsDetails,
      toggleSingleSalesResult,
      expandedFlags,
      checkboxArray,
      setCheckboxArray,
      handleLotCheckbox,
      setBidderName,
      setUserRef,
      setIsOpenSendMessageModal
    }),
    [showSalesResultsDetails]
  );

  const lotColumns = useMemo(
    () => getLotColumns({
      checkboxArray,
      setCheckboxArray,
      handleLotCheckbox,
      handleMemberCheckbox,
      canAddFeedback,
      setLoadingFeedbackModalData,
      handleEditFeedback,
      handleRemoveFeedback,
      setCurrentFeedbackLotRef,
      setIsOpenFeedbackModal,
      fetchDefaultFeedbackMessages,
      dispatch,
      houseRef,
      multiSession,
    }),
    [checkboxArray]
  );

  // Bidder summary table instance
  const bidderTable = useReactTable({
    data: tableData,
    columns: bidderColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  return (
    <Fragment>
      <div style={{ width: '1200px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', height: '1200px', overflowY: 'scroll', display: 'block', border: 'solid 1px #ddd', fontSize: '14px' }}>
          <thead>
            {bidderTable.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id} style={{ width: '100%', display: 'table-row' }}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{ border: '1px solid #ccc', padding: 4, background: '#f5f5f5', position: 'sticky', top: '0' }}
                    className={`column-width-${header.index}`}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={
                          header.column.getCanSort()
                            ? 'cursor-pointer select-none'
                            : ''
                        }
                        onClick={header.column.getToggleSortingHandler()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            header.column.getToggleSortingHandler()(e);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        title={
                          header.column.getCanSort()
                            ? (() => {
                              const nextOrder = header.column.getNextSortingOrder();
                              if (nextOrder === 'asc') return 'Sort ascending';
                              if (nextOrder === 'desc') return 'Sort descending';
                              return 'Clear sort';
                            })()
                            : undefined
                        }
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: ' ▲',
                          desc: ' ▼',
                        }[header.column.getIsSorted()] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {bidderTable.getRowModel().rows.map(row => {
              const bidder = row.original;
              return (
                <Fragment key={row.id}>
                  <tr className="bidder-row">
                    {row.getVisibleCells().map(cell => (
                      <td
                        valign="top"
                        key={cell.id}
                        style={{
                          border: '1px solid #ccc',
                          padding: 4,
                          textAlign: 'left',
                        }}
                        aria-label={`Cell ${cell.id}`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {expandedFlags[row.original.memberRef] && (
                  <tr>
                    <td colSpan={bidderTable.getAllLeafColumns().length}>
                      <div aria-label="Lot details table">
                        <LotTable
                          lots={bidder.lots}
                          columns={lotColumns}
                          memberRef={row.original.memberRef}
                          channel={row.original.channel}
                        />
                      </div>
                    </td>
                  </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Fragment>
  );
}

export default NewSalesResultsTable;
